import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPersonActivity, type PersonActivity } from "../../api/personActivity";
import { RelativeTimestamp } from "../RelativeTimestamp";
import { formatDateTime } from "../consumption/formatters";
import { ErrorState, LoadingState } from "../states";
import panel from "./AdminPanel.module.css";

/** Whole days from an ISO date (`2026-10-20`) to today, by the viewer's calendar. */
function daysSince(isoDay: string, today: Date): number {
  const [year, month, day] = isoDay.split("-").map(Number);
  const then = new Date(year, month - 1, day);
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.round((now.getTime() - then.getTime()) / 86_400_000));
}

/**
 * When the person was last active, to help find people who have stopped using their tools: the last time they used the portal itself
 * (website or a connected application), then the last day each provider recorded use for them, as tiles. A provider they never used has
 * no tile.
 */
export function PersonActivityPanel({ personId }: { personId: string }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; activity: PersonActivity } | { kind: "error"; message: string }>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchPersonActivity(personId);
    if (result.kind === "ok") {
      // Read defensively: a reply without the list is treated as no recorded use.
      const response: Partial<PersonActivity> = result.response;
      setState({ kind: "loaded", activity: { portalLastSeenAt: response.portalLastSeenAt ?? null, providers: Array.isArray(response.providers) ? response.providers : [] } });
    } else {
      setState({ kind: "error", message: result.kind === "denied" ? t("admin.entitlement.denied.description") : result.message });
    }
  }, [personId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const today = new Date();

  return (
    <section className={panel.panel} aria-labelledby="person-activity-heading">
      <div className={panel.header}>
        <h2 id="person-activity-heading" className={`panel-title ${panel.title}`}>
          {t("admin.entitlement.person.activity.title")}
        </h2>
      </div>
      {state.kind === "loading" && <LoadingState label={t("admin.entitlement.person.activity.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" && (
        <dl className={panel.tiles}>
          <div className={panel.tile}>
            <dt>{t("admin.entitlement.person.activity.portal")}</dt>
            {state.activity.portalLastSeenAt === null ? (
              <dd data-empty="">{t("admin.entitlement.person.activity.portalNever")}</dd>
            ) : (
              <dd>
                <RelativeTimestamp isoTimestamp={state.activity.portalLastSeenAt} />
                <span className={panel.tileNote}>{formatDateTime(state.activity.portalLastSeenAt)}</span>
              </dd>
            )}
          </div>
          {state.activity.providers.map((provider) => {
            const days = daysSince(provider.lastSeenDay, today);
            return (
              <div key={provider.vendorId} className={panel.tile}>
                <dt>{t("admin.entitlement.person.activity.provider", { provider: provider.vendorDisplayName })}</dt>
                <dd>
                  {days === 0 ? t("admin.entitlement.person.activity.today") : t("admin.entitlement.person.activity.daysAgo", { count: days })}
                  <span className={panel.tileNote}>{new Date(`${provider.lastSeenDay}T00:00:00`).toLocaleDateString(i18n.language, { dateStyle: "medium" })}</span>
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      {state.kind === "loaded" && state.activity.providers.length === 0 && <p className={panel.footnotes}>{t("admin.entitlement.person.activity.noProviders")}</p>}
    </section>
  );
}
